import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { FilePen, Send, FileCheck, Loader2, FilePlus2, FileText, Search } from "lucide-react";
import { DeleteButton } from "../components/DeleteButton";
import { toast } from "sonner";
import { usePaginacao } from "../hooks/usePaginacao";
import { Paginacao } from "../components/Paginacao";
import { EmptyState, SkeletonTabela } from "../components/EmptyState";
import { ExportarBotao } from "../components/ExportarBotao";

const SIG_LABEL = {
  rascunho: { label: "Rascunho", color: "#6b7280", icon: FilePen },
  aguardando: { label: "Aguardando", color: "#eab308", icon: Send },
  assinado: { label: "Assinado", color: "#16a34a", icon: FileCheck },
  recusado: { label: "Recusado", color: "#dc2626", icon: FilePen },
};

const classeLinha = (vencimento) => {
  if (!vencimento) return "";
  const dias = Math.ceil((new Date(vencimento) - new Date()) / (1000 * 60 * 60 * 24));
  if (dias < 0) return "bg-red-50";          // vencido
  if (dias <= 30) return "bg-amber-50";       // alerta
  return "";
};

export default function Contratos() {
  const {
    itens: contratos, total, pagina, totalPaginas, limite,
    carregando, filtros, irParaPagina, setFiltro, recarregar,
  } = usePaginacao("/contratos", { limite: 20, filtrosIniciais: { q: "", status: "" } });

  const [clientes, setClientes] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [aditivosOpen, setAditivosOpen] = useState(null);
  const [aditivos, setAditivos] = useState([]);
  const [novoForm, setNovoForm] = useState({ descricao: "", novo_valor: "", nova_vigencia: "", aprovado_por: "" });

  useEffect(() => {
    api.get("/clientes", { params: { limit: 200 } }).then((r) => setClientes(r.data));
  }, []);

  const abrirAditivos = async (c) => {
    setAditivosOpen(c);
    try {
      const r = await api.get(`/contratos/${c.id}/aditivos`);
      setAditivos(r.data);
    } catch {
      setAditivos([]);
    }
  };

  const criarAditivo = async () => {
    if (!novoForm.descricao || !novoForm.aprovado_por) {
      toast.error("Descrição e aprovador são obrigatórios");
      return;
    }
    const payload = { descricao: novoForm.descricao, aprovado_por: novoForm.aprovado_por };
    if (novoForm.novo_valor) payload.novo_valor = parseFloat(novoForm.novo_valor);
    if (novoForm.nova_vigencia) payload.nova_vigencia = novoForm.nova_vigencia;
    try {
      await api.post(`/contratos/${aditivosOpen.id}/aditivos`, payload);
      toast.success("Aditivo criado");
      setNovoForm({ descricao: "", novo_valor: "", nova_vigencia: "", aprovado_por: "" });
      abrirAditivos(aditivosOpen);
      recarregar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha");
    }
  };

  const cliMap = Object.fromEntries(clientes.map((c) => [c.id, c]));
  const fmt = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);
  const token = localStorage.getItem("lab_token") || "";

  const enviarAssinatura = async (c) => {
    setBusyId(c.id);
    try {
      const { data } = await api.post(`/contratos/${c.id}/gerar-pdf-e-enviar`);
      toast.success(`PDF gerado e enviado · ${data.document_uuid}`);
      recarregar();
    } catch (e) {
      toast.error("Falha ao enviar");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-display font-bold" style={{ color: "#1B3A6B" }}>Contratos</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {total.toLocaleString("pt-BR")} contratos · gere PDF e dispare assinatura digital (D4Sign)
          </p>
        </div>
        <ExportarBotao endpoint="/export/contratos" filtros={filtros} nomeArquivo="contratos" testId="exportar-contratos" />
      </div>

      <Card className="p-3 flex gap-2 flex-wrap items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            placeholder="Buscar por número ou ID…"
            value={filtros.q || ""}
            onChange={(e) => setFiltro("q", e.target.value)}
            className="pl-9 h-9 text-sm"
            data-testid="contratos-busca"
          />
        </div>
        <select
          value={filtros.status || ""}
          onChange={(e) => setFiltro("status", e.target.value)}
          className="h-9 px-3 text-sm border border-zinc-200 rounded-md bg-white"
          data-testid="contratos-filtro-status"
        >
          <option value="">Todos os status</option>
          <option value="rascunho">Rascunho</option>
          <option value="ativo">Ativo</option>
          <option value="encerrado">Encerrado</option>
          <option value="vencido">Vencido</option>
        </select>
        <div className="flex items-center gap-3 text-[11px] text-zinc-500 ml-2">
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded bg-amber-100 border border-amber-300" /> Vence em ≤30d
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded bg-red-100 border border-red-300" /> Vencido
          </span>
        </div>
      </Card>

      <Card>
        {carregando ? (
          <SkeletonTabela linhas={10} colunas={8} />
        ) : contratos.length === 0 ? (
          <EmptyState
            icone={FileText}
            titulo="Nenhum contrato encontrado"
            descricao={filtros.q ? `Nenhum resultado para "${filtros.q}".` : "Nenhum contrato cadastrado com os filtros atuais."}
          />
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Valor mensal</TableHead>
                  <TableHead>Início</TableHead>
                  <TableHead>Vencimento</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Assinatura</TableHead>
                  <TableHead>PDF</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {contratos.map((c) => {
                  const dias = c.vencimento ? Math.ceil((new Date(c.vencimento) - new Date()) / (1000 * 60 * 60 * 24)) : null;
                  const renovar = dias !== null && dias <= 30 && dias > 0;
                  const vencido = dias !== null && dias < 0;
                  const sig = SIG_LABEL[c.assinatura_status || "rascunho"];
                  const SigIcon = sig.icon;
                  return (
                    <TableRow key={c.id} data-testid={`contrato-row-${c.id}`} className={classeLinha(c.vencimento)}>
                      <TableCell className="font-medium">{cliMap[c.cliente_id]?.nome || "—"}</TableCell>
                      <TableCell className="font-semibold" style={{ color: "#F97316" }}>{fmt(c.valor_mensal)}</TableCell>
                      <TableCell className="text-xs">{c.inicio ? new Date(c.inicio).toLocaleDateString("pt-BR") : "—"}</TableCell>
                      <TableCell className="text-xs">
                        {c.vencimento ? new Date(c.vencimento).toLocaleDateString("pt-BR") : "—"}
                        {renovar && <Badge className="ml-2 bg-amber-500 text-white" data-testid={`alerta-${c.id}`}>renovar em {dias}d</Badge>}
                        {vencido && <Badge className="ml-2 bg-red-600 text-white" data-testid={`vencido-${c.id}`}>VENCIDO</Badge>}
                      </TableCell>
                      <TableCell><Badge>{c.status}</Badge></TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <SigIcon size={12} style={{ color: sig.color }} />
                          <Badge style={{ background: sig.color, color: "white" }} data-testid={`sig-${c.id}`}>
                            {sig.label}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell>
                        {c.pdf_url ? (
                          <a
                            href={`${process.env.REACT_APP_BACKEND_URL}${c.pdf_url}?auth=${token}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:text-blue-800 underline"
                            data-testid={`pdf-${c.id}`}
                          >
                            Visualizar
                          </a>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          <Button size="sm" variant="outline" onClick={() => abrirAditivos(c)} data-testid={`aditivos-${c.id}`}>
                            <FilePlus2 size={12} className="mr-1" /> Aditivos
                          </Button>
                          {(c.assinatura_status === "rascunho" || !c.assinatura_status || c.assinatura_status === "recusado") && (
                            <Button size="sm" className="text-white" style={{ background: "#1B3A6B" }} onClick={() => enviarAssinatura(c)} disabled={busyId === c.id} data-testid={`enviar-${c.id}`}>
                              {busyId === c.id ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Send size={12} className="mr-1" />}
                              PDF
                            </Button>
                          )}
                          {(c.assinatura_status === "aguardando") && (
                            <Button size="sm" variant="outline" onClick={() => enviarAssinatura(c)} disabled={busyId === c.id}>
                              Reenviar
                            </Button>
                          )}
                          <DeleteButton
                            url={`/contratos/${c.id}`}
                            label={`Contrato #${c.id.slice(0, 12)}`}
                            warningText={c.status === "ativo" ? "Este contrato está ativo e possui equipamentos em campo. A exclusão irá cancelar o contrato e liberar os equipamentos." : null}
                            onDeleted={recarregar}
                            testId={`del-contrato-${c.id}`}
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <div className="px-4">
              <Paginacao
                pagina={pagina}
                totalPaginas={totalPaginas}
                total={total}
                limite={limite}
                irParaPagina={irParaPagina}
              />
            </div>
          </>
        )}
      </Card>

      <Dialog open={!!aditivosOpen} onOpenChange={(o) => !o && setAditivosOpen(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Aditivos do contrato</DialogTitle>
            <DialogDescription>
              {aditivosOpen && (cliMap[aditivosOpen.cliente_id]?.nome || "—")}
              {" · "}
              <span className="font-semibold">{aditivosOpen && fmt(aditivosOpen.valor_mensal)}/mês</span>
            </DialogDescription>
          </DialogHeader>

          {aditivos.length > 0 && (
            <div className="border rounded-md divide-y" data-testid="aditivos-lista">
              {aditivos.map((a) => (
                <div key={a.id} className="p-3 flex items-start gap-3">
                  <Badge variant="outline" className="shrink-0">#{a.numero}</Badge>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm">{a.descricao}</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {a.novo_valor && <>Novo valor: <strong>{fmt(a.novo_valor)}</strong> · </>}
                      {a.nova_vigencia && <>Nova vigência: <strong>{new Date(a.nova_vigencia).toLocaleDateString("pt-BR")}</strong> · </>}
                      Aprovado por {a.aprovado_por} em {new Date(a.criado_em).toLocaleDateString("pt-BR")}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="border-t pt-4">
            <h3 className="text-sm font-semibold mb-3" style={{ color: "#1B3A6B" }}>Novo aditivo</h3>
            <div className="space-y-3">
              <div>
                <Label>Descrição *</Label>
                <Textarea data-testid="aditivo-form-desc" value={novoForm.descricao} onChange={(e) => setNovoForm({ ...novoForm, descricao: e.target.value })} rows={2} className="mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Novo valor mensal (opcional)</Label>
                  <Input type="number" step="0.01" data-testid="aditivo-form-valor" value={novoForm.novo_valor} onChange={(e) => setNovoForm({ ...novoForm, novo_valor: e.target.value })} placeholder="Se alterar" className="mt-1" />
                </div>
                <div>
                  <Label>Nova vigência (opcional)</Label>
                  <Input type="date" data-testid="aditivo-form-vigencia" value={novoForm.nova_vigencia} onChange={(e) => setNovoForm({ ...novoForm, nova_vigencia: e.target.value })} className="mt-1" />
                </div>
              </div>
              <div>
                <Label>Aprovado por *</Label>
                <Input data-testid="aditivo-form-aprovador" value={novoForm.aprovado_por} onChange={(e) => setNovoForm({ ...novoForm, aprovado_por: e.target.value })} className="mt-1" />
              </div>
              <Button onClick={criarAditivo} data-testid="aditivo-form-salvar" className="w-full text-white" style={{ background: "#F97316" }}>
                Registrar aditivo
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
